import React, { createContext, useState, useEffect, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import {
  LANGUAGE_CODES,
  detectBrowserLanguage,
  DEFAULT_LANGUAGE,
  getLanguageUrl,
  getUrlLanguage,
} from '../config/languages';
import English from '../locales/en.json';
import Japanese from '../locales/ja.json';
import SimplifiedChinese from '../locales/zh-CN.json';
import TraditionalChinese from '../locales/zh-TW.json';
import HongKongChinese from '../locales/zh-HK.json';
import Korean from '../locales/ko.json';
import Thai from '../locales/th.json';
import Indonesian from '../locales/id.json';
import Vietnamese from '../locales/vi.json';
import Spanish from '../locales/es.json';
import French from '../locales/fr.json';
import Italian from '../locales/it.json';
import German from '../locales/de.json';
import { fontLoader } from '../utils/dynamicFontLoader';
import { debounceFontLoad } from '../utils/debounce';

export const LanguageContext = createContext();

// メッセージファイルのマップ
const messagesMap = {
  ja: Japanese,
  en: English,
  'zh-CN': SimplifiedChinese,
  'zh-TW': TraditionalChinese,
  'zh-HK': HongKongChinese,
  ko: Korean,
  th: Thai,
  id: Indonesian,
  vi: Vietnamese,
  es: Spanish,
  fr: French,
  it: Italian,
  de: German,
};

const LanguageProvider = ({ children }) => {
  const [locale, setLocale] = useState(() => {
    const urlLanguage = getUrlLanguage();
    if (urlLanguage) return urlLanguage;
    // ローカルストレージから言語設定を読み込む
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && LANGUAGE_CODES.includes(savedLanguage)) {
      return savedLanguage;
    }

    // ブラウザの言語設定を検出
    return detectBrowserLanguage();
  });

  // locale に基づいてメッセージを設定
  const [messages, setMessages] = useState(() => {
    return messagesMap[locale] || messagesMap[DEFAULT_LANGUAGE];
  });

  // デバウンスされたフォント読み込み関数（パフォーマンス向上）
  const debouncedFontLoad = useMemo(
    () => debounceFontLoad(locale => fontLoader.loadFont(locale), 300),
    []
  );

  // locale が変更されたときにローカルストレージを更新
  useEffect(() => {
    localStorage.setItem('language', locale);
  }, [locale]);

  // locale が変更されたときにフォントを読み込む（デバウンス適用）
  useEffect(() => {
    debouncedFontLoad(locale);
  }, [locale, debouncedFontLoad]);

  // 初期化時に基本フォントを読み込む
  useEffect(() => {
    fontLoader.preloadBasicFonts();
  }, []);

  // 言語を切り替える関数
  const switchLanguage = lang => {
    if (!LANGUAGE_CODES.includes(lang)) return;
    window.history.replaceState(
      window.history.state,
      '',
      getLanguageUrl(lang, window.location.href)
    );
    setLocale(lang);
    setMessages(messagesMap[lang] || messagesMap[DEFAULT_LANGUAGE]);
  };

  return (
    <LanguageContext.Provider value={{ locale, switchLanguage }}>
      <IntlProvider messages={messages} locale={locale}>
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
